import SEOHead from "@/components/SEOHead";

const Privacy = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <SEOHead title="Privacy Policy — Lumient" description="How Lumient collects, uses, and protects your personal data. Read our full privacy policy." canonical="https://lumient.ai/privacy" />
    <main className="flex-1 px-6 lg:px-16 pt-32 pb-24 max-w-3xl mx-auto w-full">
      <h1 className="text-4xl font-semibold tracking-tight mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-12">Last updated: 27 March 2026</p>

      <div className="space-y-10 text-muted-foreground text-sm leading-relaxed">

        <Section title="1. Who we are">
          <p>
            Lumient AI ("Lumient", "we", "us") is an early-stage software company building
            operational intelligence infrastructure for knowledge work. This Privacy Policy applies
            to data collected through our website at <strong>lumient.ai</strong> and through any
            pilot or early-access engagements we conduct with customers.
          </p>
          <p>
            Data controller: Lumient AI — for any privacy-related enquiries contact us at{" "}
            <a href="mailto:privacy@lumient.ai" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">
              privacy@lumient.ai
            </a>.
          </p>
        </Section>

        <Section title="2. What data we collect">
          <p>We collect only what is necessary for the purposes described below:</p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li>
              <strong className="text-foreground">Contact and inquiry data</strong> — name, email
              address, company name, job title, and any message content you submit via our contact
              form or email.
            </li>
            <li>
              <strong className="text-foreground">Newsletter subscriptions</strong> — email address
              you voluntarily provide when opting in to product updates.
            </li>
            <li>
              <strong className="text-foreground">Website analytics</strong> — aggregate,
              anonymised page-view and referrer data to understand how visitors discover and use our
              site. We do not use cross-site tracking or fingerprinting.
            </li>
            <li>
              <strong className="text-foreground">Pilot engagement data</strong> — documents, files,
              or structured data you share with us during a pilot. This is processed under a
              separate Data Processing Agreement (DPA) and is not covered by this general policy.
            </li>
          </ul>
        </Section>

        <Section title="3. Legal basis for processing (GDPR)">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong className="text-foreground">Consent</strong> — for newsletter subscriptions
              and any optional data processing you explicitly opt into.
            </li>
            <li>
              <strong className="text-foreground">Legitimate interests</strong> — for responding to
              contact enquiries, maintaining basic website analytics, and communicating product
              updates to prospective customers who have expressed interest.
            </li>
            <li>
              <strong className="text-foreground">Contractual necessity</strong> — for data
              processed during a pilot engagement under a signed agreement.
            </li>
          </ul>
        </Section>

        <Section title="4. How we use your data">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>To respond to contact form submissions and pilot requests.</li>
            <li>To send product updates and early-access announcements to newsletter subscribers.</li>
            <li>To understand how the website is used and improve it over time.</li>
            <li>To fulfil our obligations under any pilot or evaluation agreement.</li>
          </ul>
          <p className="mt-3">
            We do <strong className="text-foreground">not</strong> sell, rent, or share your
            personal data with third parties for advertising or marketing purposes.
          </p>
        </Section>

        <Section title="5. Data sharing and sub-processors">
          <p>
            We use a small number of trusted third-party services to operate our product and
            website. Each is bound by a data processing agreement with us:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li>
              <strong className="text-foreground">Supabase</strong> — database and authentication
              infrastructure (EU region).
            </li>
            <li>
              <strong className="text-foreground">Resend / Postmark</strong> — transactional email
              delivery for contact confirmations.
            </li>
            <li>
              <strong className="text-foreground">Vercel</strong> — website hosting and edge
              delivery.
            </li>
          </ul>
          <p className="mt-3">
            We will update this list as our sub-processor landscape changes and notify active pilot
            users of any material changes.
          </p>
        </Section>

        <Section title="6. Data retention">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              Contact form submissions are retained for up to 24 months, after which they are
              deleted unless an ongoing commercial relationship exists.
            </li>
            <li>
              Newsletter subscriber emails are retained until you unsubscribe, at which point they
              are deleted within 30 days.
            </li>
            <li>
              Pilot engagement data is deleted or returned to you within 30 days of the engagement
              ending, unless a longer retention period is agreed in writing.
            </li>
            <li>
              Anonymised analytics data may be retained indefinitely as it cannot be linked to any
              individual.
            </li>
          </ul>
        </Section>

        <Section title="7. Cookies">
          <p>
            We use no third-party advertising or tracking cookies. Our website may set a single
            first-party session cookie for security and functional purposes (e.g. CSRF protection).
            This cookie expires when you close your browser and contains no personally identifiable
            information.
          </p>
        </Section>

        <Section title="8. Your rights under GDPR">
          <p>
            If you are located in the European Economic Area, the United Kingdom, or Switzerland,
            you have the following rights regarding your personal data:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2">
            <li><strong className="text-foreground">Access</strong> — request a copy of the data we hold about you.</li>
            <li><strong className="text-foreground">Rectification</strong> — ask us to correct inaccurate or incomplete data.</li>
            <li><strong className="text-foreground">Erasure</strong> — request deletion of your personal data ("right to be forgotten").</li>
            <li><strong className="text-foreground">Portability</strong> — receive your data in a structured, machine-readable format.</li>
            <li><strong className="text-foreground">Restriction</strong> — ask us to restrict processing in certain circumstances.</li>
            <li><strong className="text-foreground">Objection</strong> — object to processing based on legitimate interests.</li>
            <li><strong className="text-foreground">Withdraw consent</strong> — where processing is based on consent, withdraw it at any time without affecting prior processing.</li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, email{" "}
            <a href="mailto:privacy@lumient.ai" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">
              privacy@lumient.ai
            </a>. We will respond within 30 days. If you believe we have handled your data
            unlawfully, you have the right to lodge a complaint with your local data protection
            authority.
          </p>
        </Section>

        <Section title="9. International transfers">
          <p>
            Our primary infrastructure is hosted within the EU. Where data is processed outside the
            EEA (e.g. by a US-based sub-processor), we ensure appropriate safeguards are in place,
            including Standard Contractual Clauses (SCCs) approved by the European Commission.
          </p>
        </Section>

        <Section title="10. Changes to this policy">
          <p>
            We may update this policy as our product and data practices evolve. The "last updated"
            date at the top of this page will reflect any changes. If you are an active pilot user
            or newsletter subscriber and we make a material change, we will notify you by email at
            least 14 days before the change takes effect.
          </p>
        </Section>

      </div>
    </main>
  </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-3">
    <h2 className="text-base font-semibold text-foreground">{title}</h2>
    {children}
  </section>
);

export default Privacy;
